import { Anchor, Card, Checkbox, Group, Stack } from '@mantine/core';
import { Link } from 'react-router-dom';
import { rowLabel } from '../../domain/rowLabel.js';
import type { Selection } from '../../hooks/useSelection.js';
import { RecordLabel } from '../Diff/RecordLabel.js';
import { EnvPills } from './EnvPills.js';
import { LoadMoreFooter } from './LoadMoreFooter.js';
import type { MatrixRow, RowSpec } from './rowSpecs.js';
import type { MatrixColumn, OpenTarget, Paging } from './types.js';

/**
 * A grid at 375px: one card per record. The top line is the stacked label (a link to the editor)
 * with the checkbox at the right, then the row's one extra fact (`spec.subtitle`), then a pill
 * per environment. The same footer and *Load more* as the grid.
 */
export const MatrixCardList = <Row extends MatrixRow>({
    spec,
    columns,
    rows,
    selection,
    onOpen,
    paging,
}: {
    spec: RowSpec<Row>;
    columns: MatrixColumn[];
    rows: Row[];
    selection: Selection;
    onOpen: (target: OpenTarget) => void;
    paging: Paging;
}) => (
    <Stack gap="xs">
        {rows.map((row) => {
            const name = rowLabel(row.title, row.key).label;
            return (
                <Card key={row.key} p="sm">
                    <Group justify="space-between" wrap="nowrap" align="flex-start" mb="xs">
                        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                            <Anchor
                                component={Link}
                                to={spec.editHref(row)}
                                underline="never"
                                c="inherit"
                                aria-label={`Edit ${name}`}
                                display="block"
                                miw={0}
                            >
                                <RecordLabel layout="stacked" title={row.title} entityKey={row.key} />
                            </Anchor>
                            {spec.subtitle(row)}
                        </Stack>
                        <Checkbox
                            aria-label={spec.selectLabel(name)}
                            checked={selection.has(row)}
                            onChange={() => selection.toggle(spec.selectionOf(row))}
                        />
                    </Group>
                    <Group gap={6} wrap="wrap">
                        <EnvPills
                            columns={columns}
                            cells={row.cells}
                            type={spec.type}
                            entityKey={row.key}
                            name={name}
                            onOpen={onOpen}
                        />
                    </Group>
                </Card>
            );
        })}
        <LoadMoreFooter
            shown={rows.length}
            total={paging.total}
            filtered={paging.filtered}
            hasMore={paging.hasMore}
            isLoadingMore={paging.isLoadingMore}
            onLoadMore={paging.onLoadMore}
        />
    </Stack>
);
